import React from 'react';
import { twMerge } from 'tailwind-merge';
import { useAppSelector } from 'flow/hooks/useRedux';
import { selectExtensionKeyFromContactKey } from 'flow/selectors/ExtensionSelectors';
import {
    selectOriginDTOSubtype,
    selectOriginVisitorId,
} from 'flow/selectors/TicketsSelectors';
import { TicketKey } from 'flow/types/EntityKeys';
import { TypedMap } from 'flow/types/TypedMap';
import { ContactFull } from 'flow/types/Contact';
import { getContactName } from 'flow/utils/contact-utils';
import {
    originContactIsPhoneNumber,
    TICKET_ORIGIN,
} from 'flow/utils/ticket-channel-utils';
import CardCallDetailsContainer from 'flow/components/pbx/v2/misc/CardCallDetailsContainer';
import ContactCardAvatar from './ContactCardAvatar';
import ContactCardTitle from './ContactCardTitle';
import ContactCardSubtitle from './ContactCardSubtitle';

interface Props {
    ticketKey: TicketKey;
    contact?: TypedMap<ContactFull>;
    inPanel: boolean;
}

/**
 * Card 1 — avatar + name/secondary. Colleagues (contacts that map to an
 * internal extension) show their current presence profile (name + end time)
 * as the second row and a status indicator on the avatar. When the visitor is
 * an email address with no matching contact, the email becomes the title.
 */
const ContactSummaryCard = ({ ticketKey, contact, inPanel }: Props) => {
    const contactFullName = getContactName(contact) ?? '';

    const visitorNumber = useAppSelector((state) =>
        selectOriginVisitorId(state, ticketKey),
    );
    const originDtoSubtype = useAppSelector((state) =>
        selectOriginDTOSubtype(state, ticketKey),
    );
    const originIsEmail = originDtoSubtype === TICKET_ORIGIN.EMAIL;

    // The number shown in the title/subtitle: the call ticket's visitor
    // number (`origin.visitorId`) when present, otherwise the contact's fixed
    // number — which non-call channels (e.g. WhatsApp) populate on
    // `visitorDetails`. Only on channels where the visitor is a phone number:
    // an email ticket shows the address as the title instead, and an unknown
    // channel shows no number at all (nothing this client could call).
    const headerNumber = originContactIsPhoneNumber(originDtoSubtype)
        ? (visitorNumber ?? contact?.get('fixed'))
        : undefined;

    const contactKey = contact?.get('key');
    const colleagueKey = useAppSelector((state) =>
        contactKey ? selectExtensionKeyFromContactKey(state, contactKey) : null,
    );

    // On WhatsApp and email tickets a bound contact (a colleague) shows
    // initials instead of their own picture, as the ticket list and message
    // avatars do for these channels.
    const ignoreAvatarResource =
        (originDtoSubtype === TICKET_ORIGIN.WHATSAPP || originIsEmail) &&
        contact?.get('type') === 'bound';

    return (
        // `badge-bg` makes the avatar's presence-indicator ring pick up the
        // card background (gray-150, or gray-200 in the panel) so its edge
        // blends into the card instead of showing the default gray-100 halo.
        <CardCallDetailsContainer
            className={twMerge(
                'badge-bg items-center gap-3 rounded-xl',
                inPanel && 'bg-gray-200',
            )}
        >
            <ContactCardAvatar
                contact={contact}
                contactFullName={contactFullName}
                colleagueKey={colleagueKey}
                inPanel={inPanel}
                ignoreAvatarResource={ignoreAvatarResource}
            />
            <div className="flex min-w-0 flex-1 flex-col justify-center text-gray-900">
                <ContactCardTitle
                    ticketKey={ticketKey}
                    contact={contact}
                    contactFullName={contactFullName}
                    headerNumber={headerNumber}
                />
                <ContactCardSubtitle
                    contact={contact}
                    contactFullName={contactFullName}
                    headerNumber={headerNumber}
                    colleagueKey={colleagueKey}
                />
            </div>
        </CardCallDetailsContainer>
    );
};

export default React.memo(ContactSummaryCard);
